import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Search, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

function formatApiError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [consentPrivacy, setConsentPrivacy] = useState(false);
  const [consentEmail, setConsentEmail] = useState(false);
  const [consentSms, setConsentSms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const guestSessionId = location.state?.guestSessionId || localStorage.getItem('localluc_guest_session') || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (!consentPrivacy) { setError('You must accept the Privacy Policy to create an account'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/register`, {
        email, password, name,
        consent_privacy: consentPrivacy,
        consent_email: consentEmail,
        consent_sms: consentSms,
        guest_session_id: guestSessionId
      }, { withCredentials: true });
      await checkAuth();
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F9FC] flex">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1709684459045-29b79a370117?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxwaXp6YSUyMHNob3AlMjBzdG9yZWZyb250fGVufDB8fHx8MTc3NTg0MDM4NXww&ixlib=rb-4.1.0&q=85"
          alt="Restaurant"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0F2951]/70" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <div className="flex items-center gap-3 mb-6">
            <img src="/icon-nav.png" alt="Local Luc" className="h-14 w-14 rounded-xl object-cover" />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight mb-4">
            Your local SEO command center
          </h1>
          <p className="text-lg text-white/80 max-w-md">
            From intention to action — audit your online presence and outrank the competition.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <img src="/icon-nav.png" alt="Local Luc" className="h-12 w-12 rounded-xl object-cover" />
          </div>

          <h2 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-[#0F2951] mb-2">
            Create your account
          </h2>
          <p className="text-base text-[#64748B] mb-8">
            Start auditing your local SEO in minutes
          </p>

          {error && (
            <div data-testid="register-error" className="mb-6 p-4 bg-[#EF4444]/10 border border-[#EF4444]/20 rounded-xl text-[#EF4444] text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-xs font-bold uppercase tracking-[0.15em] text-[#64748B]">Full Name</Label>
              <Input data-testid="register-name-input" id="name" value={name} onChange={e => setName(e.target.value)} placeholder="John Doe" required className="border-[#E2E8F0] bg-white rounded-xl focus:ring-2 focus:ring-[#0268C4]/20 h-12" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-bold uppercase tracking-[0.15em] text-[#64748B]">Email</Label>
              <Input data-testid="register-email-input" id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required className="border-[#E2E8F0] bg-white rounded-xl focus:ring-2 focus:ring-[#0268C4]/20 h-12" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-bold uppercase tracking-[0.15em] text-[#64748B]">Password</Label>
              <Input data-testid="register-password-input" id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 6 characters" required className="border-[#E2E8F0] bg-white rounded-xl focus:ring-2 focus:ring-[#0268C4]/20 h-12" />
            </div>

            {/* Legal Consent */}
            <div className="space-y-3 pt-2">
              <div className="flex items-start gap-3">
                <Checkbox
                  data-testid="consent-privacy-checkbox"
                  id="consent-privacy"
                  checked={consentPrivacy}
                  onCheckedChange={setConsentPrivacy}
                  className="mt-0.5"
                />
                <label htmlFor="consent-privacy" className="text-sm text-[#64748B] leading-snug cursor-pointer">
                  I agree to the <Link to="/privacy" className="text-[#0268C4] underline" target="_blank">Privacy Policy</Link> and <Link to="/terms" className="text-[#0268C4] underline" target="_blank">Terms of Service</Link>. I understand my data is retained for 30 days. <span className="text-[#EF4444]">*</span>
                </label>
              </div>
              <div className="flex items-start gap-3">
                <Checkbox
                  data-testid="consent-email-checkbox"
                  id="consent-email"
                  checked={consentEmail}
                  onCheckedChange={setConsentEmail}
                  className="mt-0.5"
                />
                <label htmlFor="consent-email" className="text-sm text-[#64748B] leading-snug cursor-pointer">
                  I'd like to receive SEO tips and product updates via email. You can unsubscribe at any time. (CAN-SPAM compliant)
                </label>
              </div>
              <div className="flex items-start gap-3">
                <Checkbox
                  data-testid="consent-sms-checkbox"
                  id="consent-sms"
                  checked={consentSms}
                  onCheckedChange={setConsentSms}
                  className="mt-0.5"
                />
                <label htmlFor="consent-sms" className="text-sm text-[#64748B] leading-snug cursor-pointer">
                  I consent to receive occasional SMS notifications. Reply STOP to opt out. Msg & data rates may apply. (TCPA compliant)
                </label>
              </div>
            </div>

            <Button data-testid="register-submit-button" type="submit" disabled={loading} className="w-full h-12 bg-[#0268C4] hover:bg-[#015BA8] text-white rounded-xl font-medium text-base transition-all duration-200 hover:-translate-y-[1px]">
              {loading ? 'Creating account...' : 'Create account'}
              {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-[#64748B]">
            Already have an account?{' '}
            <Link data-testid="login-link" to="/login" className="text-[#0268C4] hover:text-[#015BA8] font-medium">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
